import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Printer, ExternalLink } from "lucide-react";
import { roadmapPublic } from "@/lib/afmApi";
import {
  PHASES, KINDS, PLATFORMS, ProgressBar, buildEvents, fmtDate, moneyMap, money, daysUntil, todayIso,
  PhasePipeline, PipelineLegend, Gantt, Agenda, AgendaList, MoneyBars,
} from "@/components/roadmap/shared";

const CATS = { produccion: "Producción", mezcla: "Mezcla", master: "Máster", video: "Video", portada: "Portada / arte", marketing: "Marketing", prensa: "Prensa / PR", estudio: "Estudio", viajes: "Viajes", adelanto: "Adelanto al artista", otros: "Otros" };
const ROLES = { compositor: "Compositor/a", letrista: "Letrista", productor: "Productor/a", interprete: "Intérprete", featuring: "Featuring", ingeniero: "Ingeniero/a", arreglista: "Arreglista", director: "Director/a", disenador: "Diseñador/a", otro: "Otro" };

function Block({ title, children, sub }) {
  return (
    <section className="space-y-3 break-inside-avoid-page">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-200 pb-2">
        <h2 className="font-serif text-2xl font-bold tracking-tight">{title}</h2>
        {sub && <span className="text-xs text-slate-500">{sub}</span>}
      </div>
      {children}
    </section>
  );
}

// Vista de solo lectura que se comparte por enlace (sin cuenta). "Descargar PDF" usa la impresión del
// navegador con un diseño pensado para papel: el PDF sale igual a lo que se ve.
export default function HojaPublica() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    roadmapPublic(token).then((r) => { setD(r); document.title = `${r.title} · Hoja de ruta`; })
      .catch((e) => setErr(e?.response?.data?.detail || "No pudimos abrir esta hoja de ruta."));
  }, [token]);
  useEffect(() => {
    if (d && params.get("pdf") === "1") { const t = setTimeout(() => window.print(), 700); return () => clearTimeout(t); }
    return undefined;
  }, [d, params]);

  // La vista pública trae el estado final de cada fase; se arma la hoja con esos datos.
  const doc = useMemo(() => (d ? { ...d, songs: (d.songs || []).map((s) => ({ ...s, phases: Object.fromEntries(PHASES.map(([k]) => [k, { status: s.effective?.[k]?.status || "pendiente", date: s.effective?.[k]?.date || "" }])) })) } : null), [d]);
  const effective = useMemo(() => Object.fromEntries((d?.songs || []).map((s) => [s.id, s.effective || {}])), [d]);
  const events = useMemo(() => (doc ? buildEvents(doc, effective) : []), [doc, effective]);

  if (err) return <div className="flex min-h-screen items-center justify-center bg-white p-6 text-center text-sm text-slate-600">{err}</div>;
  if (!d) return <div className="flex min-h-screen items-center justify-center bg-white"><div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-black" /></div>;
  const prog = d.songs.length ? Math.round(d.songs.reduce((a, s) => a + (s.progress || 0), 0) / d.songs.length) : 0;
  const next = d.songs.map((s) => s.release_date).filter((x) => x && x >= todayIso()).sort()[0];
  const moneyRows = d.songs.map((s) => ({ id: s.id, title: s.title, cost: s.cost_usd || 0, income: s.income_usd || 0 }));

  return (
    <div className="min-h-screen bg-white text-black" data-testid="roadmap-public">
      <style>{`@media print { @page { size: A4; margin: 12mm; } .no-print { display: none !important; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } tr, article { page-break-inside: avoid; } }`}</style>
      <div className="no-print sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white/90 px-4 py-2 backdrop-blur">
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Always Fre$h Music · Hoja de ruta</span>
        <button onClick={() => window.print()} className="flex items-center gap-1.5 rounded-lg bg-black px-3 py-1.5 text-sm font-semibold text-white"><Printer className="h-4 w-4" /> Descargar PDF</button>
      </div>
      <div className="mx-auto max-w-6xl space-y-10 px-4 py-8 sm:px-8">
        <header className="space-y-4">
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#CA0808]">{d.manager_name ? `${d.manager_name} × ` : ""}{d.artist_name}</div>
          <h1 className="font-serif text-4xl font-bold tracking-tight sm:text-5xl">{d.title}</h1>
          {d.description && <p className="max-w-3xl whitespace-pre-line text-sm text-slate-700">{d.description}</p>}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl border border-slate-200 p-3"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Proyecto</div><div className="text-lg font-bold">{KINDS[d.kind] || "Proyecto"} · {d.songs.length}</div><div className="text-xs text-slate-500">canción{d.songs.length === 1 ? "" : "es"}</div></div>
            <div className="rounded-xl border border-slate-200 p-3"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Avance</div><div className="text-lg font-bold">{prog}%</div><ProgressBar value={prog} className="mt-1" /></div>
            <div className="rounded-xl border border-slate-200 p-3"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Próxima salida</div><div className="text-lg font-bold">{next ? fmtDate(next) : "—"}</div><div className="text-xs text-slate-500">{next ? `en ${daysUntil(next)} días` : ""}</div></div>
            <div className="rounded-xl border border-slate-200 p-3"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Actualizada</div><div className="text-lg font-bold">{d.updated_at ? fmtDate(String(d.updated_at).slice(0, 10)) : "—"}</div></div>
          </div>
        </header>

        <Block title="Línea de tiempo" sub="Cada barra va de la primera entrega a la salida (♦)">
          <Gantt doc={doc} effective={effective} />
        </Block>

        <Block title="Lo que viene" sub="Próximos 30 días">
          <Agenda events={events} />
        </Block>

        <Block title="Canciones">
          <PipelineLegend />
          <div className="space-y-4">
            {d.songs.map((s, i) => (
              <article key={s.id} className="space-y-3 rounded-xl border border-slate-200 p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-serif text-2xl font-bold">{i + 1}. {s.title}</h3>
                    <div className="text-sm text-slate-500">{s.release_date ? `Sale el ${fmtDate(s.release_date, true)}` : "Fecha por definir"} · {s.progress}% listo</div>
                  </div>
                  <PhasePipeline song={doc.songs[i]} effective={effective[s.id]} />
                </div>
                {s.notes && <p className="whitespace-pre-line text-sm text-slate-700">{s.notes}</p>}
                {Object.keys(s.store_links || {}).length > 0 && (
                  <div className="flex flex-wrap gap-2">{Object.entries(s.store_links).slice(0, 8).map(([k, u]) => <a key={k} href={u} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-2 py-0.5 text-xs font-semibold capitalize">{k}<ExternalLink className="h-3 w-3" /></a>)}</div>
                )}
                <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                  {s.credits?.length > 0 && <div><dt className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Créditos</dt><dd>{s.credits.map((c) => `${c.name} (${ROLES[c.role] || c.role})`).join(" · ")}</dd></div>}
                  {(s.video?.concept || s.video?.due) && <div><dt className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Video</dt><dd>{s.video.concept}{s.video.due ? ` · entrega ${fmtDate(s.video.due)}` : ""}</dd></div>}
                  {(s.cover?.concept || s.cover?.due) && <div><dt className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Portada</dt><dd>{s.cover.concept}{s.cover.due ? ` · entrega ${fmtDate(s.cover.due)}` : ""}</dd></div>}
                </dl>
                {s.marketing?.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Contenido</div>
                    {s.marketing.map((m, k) => (
                      <div key={k} className="flex flex-wrap gap-x-3 text-xs"><span className="w-24 font-semibold">{fmtDate(m.date) || "—"}</span>{PLATFORMS.filter(([p]) => m[p]).map(([p, l]) => <span key={p}><span className="text-pink-700">{l}:</span> {m[p]}</span>)}</div>
                    ))}
                  </div>
                )}
                {d.show_budget && (s.budget || []).length > 0 && (
                  <div className="text-xs text-slate-600">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Presupuesto</div>
                    {(s.budget || []).map((b, k) => <div key={k}>{CATS[b.cat] || b.cat}: {b.concept || "—"} · {b.amount != null ? money(b.amount, b.currency) : "por definir"}{b.paid ? " · pagado" : ""}{b.recoupable ? " · se recupera" : ""}</div>)}
                  </div>
                )}
              </article>
            ))}
          </div>
        </Block>

        {(d.show_budget || d.show_income) && (
          <Block title="Dinero" sub={d.show_budget ? `Invertido en total: ${moneyMap(d.cost_total)}` : ""}>
            {d.show_budget && d.show_income && <MoneyBars rows={moneyRows} />}
            {d.recoup && !d.recoup.hidden && d.recoup.recoupable_usd > 0 && (
              <div className="rounded-xl border border-slate-200 p-4 text-sm">
                <strong>Recoup:</strong> recuperado {money(d.recoup.recouped_usd)} de {money(d.recoup.recoupable_usd)} ({d.recoup.pct}%). {d.recoup.pending_usd > 0 ? `Faltan ${money(d.recoup.pending_usd)}.` : "Cubierto."}
                <ProgressBar value={d.recoup.pct} className="mt-2" />
              </div>
            )}
          </Block>
        )}

        <Block title="Agenda completa">
          <AgendaList events={events} songs={d.songs} readOnly openAll />
        </Block>
        <footer className="border-t border-slate-200 pt-4 text-[11px] text-slate-400">Hoja de ruta hecha con Always Fresh Music · Powered by SONA. Documento de solo lectura.</footer>
      </div>
    </div>
  );
}
